import React, { useState, useMemo } from 'react';
import { Icon, Badge, Input } from '@/components/atoms';
import { BatchDetail } from './BatchDetail';
import { useBatchesListQuery } from '@/hooks/useErpApi';

interface BatchListProps {
  warehouseId?: string;
  variantId?: string;
}

export const BatchList: React.FC<BatchListProps> = ({ warehouseId, variantId }) => {
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [warehouseFilter, setWarehouseFilter] = useState<string>(warehouseId || 'all');
  const [expiryFilter, setExpiryFilter] = useState<string>('all');

  const { data: batchesResponse, isLoading, error, refetch } = useBatchesListQuery({
    warehouse_id: warehouseId,
    variant_id: variantId,
    // Remove in_stock_only filter to show all batches
  });

  // Extract batches from response
  // useQuery returns response.data, so batchesResponse is already the data object
  // API response structure: { success: true, data: { batches: [...], pagination: {...}, summary: {...} } }
  // So batchesResponse = { batches: [...], pagination: {...}, summary: {...} }
  const batches = useMemo(() => {
    if (!batchesResponse) return [];
    
    const responseData = batchesResponse as any;
    
    // Check if it's the nested structure with batches array (most likely)
    if (responseData?.batches && Array.isArray(responseData.batches)) {
      return responseData.batches;
    }
    
    // Check if it's nested under data property (fallback)
    if (responseData?.data?.batches && Array.isArray(responseData.data.batches)) {
      return responseData.data.batches;
    }
    
    // Check if it's a direct array (fallback)
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    return [];
  }, [batchesResponse]);

  // Calculate expiry status and color
  const getExpiryStatus = (batch: any): { status: string; color: string; days: number } => {
    const expiryDate = batch.batch_info?.expiry_date || batch.expiry_date;
    if (!expiryDate) {
      return { status: 'No Expiry', color: 'info', days: Infinity };
    }
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'Expired', color: 'error', days: diffDays };
    } else if (diffDays <= 30) {
      return { status: 'Critical', color: 'error', days: diffDays };
    } else if (diffDays <= 60) {
      return { status: 'Warning', color: 'warning', days: diffDays };
    } else {
      return { status: 'Good', color: 'success', days: diffDays };
    }
  };

  // Get quantity status
  const getQuantityStatus = (currentQty: number, originalQty: number): string => {
    const percentage = (currentQty / originalQty) * 100;
    return percentage < 10 ? 'low' : 'normal';
  };

  // Filter batches
  const filteredBatches = useMemo(() => {
    if (!batches) return [];

    return batches.filter((batch: any) => {
      // Search filter (product name, variant name)
      const productName = batch.product?.name || '';
      const variantName = batch.variant?.variant_name || batch.variant_id || '';
      const matchesSearch =
        !searchQuery ||
        productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        variantName.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const availableQty = batch.quantity_details?.available_quantity ?? batch.total_quantity ?? 0;
      const batchStatus = availableQty > 0 ? 'active' : 'used_up';
      const matchesStatus = statusFilter === 'all' || batchStatus === statusFilter;

      // Warehouse filter
      const batchWarehouseId = batch.warehouse?.id || batch.warehouse_id;
      const matchesWarehouse = warehouseFilter === 'all' || batchWarehouseId === warehouseFilter;

      // Variant filter
      const batchVariantId = batch.variant?.id || batch.variant_id;
      const matchesVariant = !variantId || batchVariantId === variantId;

      // Expiry filter
      let matchesExpiry = true;
      if (expiryFilter !== 'all') {
        const { days } = getExpiryStatus(batch);
        if (expiryFilter === 'expired') matchesExpiry = days < 0;
        else if (expiryFilter === 'critical') matchesExpiry = days >= 0 && days <= 30;
        else if (expiryFilter === 'warning') matchesExpiry = days > 30 && days <= 60;
        else if (expiryFilter === 'good') matchesExpiry = days > 60;
      }

      return matchesSearch && matchesStatus && matchesWarehouse && matchesVariant && matchesExpiry;
    });
  }, [batches, searchQuery, statusFilter, warehouseFilter, variantId, expiryFilter]);

  const toggleExpandRow = (batchId: string) => {
    setExpandedBatchId(expandedBatchId === batchId ? null : batchId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading batches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="bg-error-100 p-2 rounded-lg">
            <Icon name="CircleAlert" size={24} className="text-error-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-error-900 mb-1">Failed to Load Batches</h3>
            <p className="text-error-700 mb-3">
              {error instanceof Error ? error.message : 'Unable to fetch batch data. Please try again.'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Debug: Log response structure (remove in production)
  if (batchesResponse && batches.length === 0) {
    console.log('[BatchList] Response structure:', {
      batchesResponse,
      batchesLength: batches.length,
      filteredLength: filteredBatches.length,
    });
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Variant
            </label>
            <Input
              placeholder="Search by variant ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="used_up">Used Up</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Expiry</label>
            <select
              value={expiryFilter}
              onChange={(e) => setExpiryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Expiry Dates</option>
              <option value="expired">Expired</option>
              <option value="critical">Critical (&lt;30 days)</option>
              <option value="warning">Warning (30-60 days)</option>
              <option value="good">Good (&gt;60 days)</option>
            </select>
          </div>

          {!warehouseId && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Warehouse</label>
              <select
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Warehouses</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Batches Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Batch ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Quantity Balance
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Expiry Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Warehouse Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredBatches.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Icon name="Package" size={48} className="text-neutral-300 mb-3" />
                      <p className="text-neutral-600 font-medium">No batches found</p>
                      <p className="text-neutral-500 text-sm mt-1">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredBatches.map((batch: any) => {
                  const expiryInfo = getExpiryStatus(batch);
                  const availableQty = batch.quantity_details?.available_quantity ?? batch.total_quantity ?? 0;
                  const totalQty = batch.quantity_details?.total_quantity ?? batch.total_quantity ?? 0;
                  const qtyStatus = getQuantityStatus(availableQty, totalQty);
                  const isExpanded = expandedBatchId === batch.id;

                  return (
                    <React.Fragment key={batch.id}>
                      <tr
                        className="hover:bg-neutral-50 transition-colors cursor-pointer"
                        onClick={() => toggleExpandRow(batch.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Icon
                              name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                              size={16}
                              className="text-neutral-400"
                            />
                            <span className="text-sm font-mono font-medium text-neutral-900">
                              {batch.id.substring(0, 12)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-neutral-900">
                            {(batch as any).product?.name || batch.variant_id || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`text-sm font-semibold ${
                              qtyStatus === 'low' ? 'text-error-600' : 'text-neutral-900'
                            }`}
                          >
                            {(batch as any).quantity_details?.available_quantity ?? batch.total_quantity ?? 0}
                            {qtyStatus === 'low' && (
                              <Badge variant="error" className="ml-2">
                                Low Stock
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {batch.batch_info?.expiry_date || batch.expiry_date ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-neutral-900">
                                {new Date(batch.batch_info?.expiry_date || batch.expiry_date).toLocaleDateString()}
                              </span>
                              <Badge
                                variant={
                                  expiryInfo.color as 'success' | 'warning' | 'error' | 'info'
                                }
                              >
                                {expiryInfo.status}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-sm text-neutral-500">No expiry</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-neutral-900">
                            {(batch as any).warehouse?.name || batch.warehouse_id || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={availableQty > 0 ? 'success' : 'error'}>
                            {availableQty > 0 ? 'Active' : 'Used Up'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpandRow(batch.id);
                            }}
                            className="text-primary-600 hover:text-primary-900 p-2 rounded-lg hover:bg-primary-50 transition-colors"
                          >
                            <Icon name="Eye" size={16} />
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-neutral-50">
                            <BatchDetail batch={batch} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
          <div className="text-sm text-neutral-600">
            Showing <span className="font-medium">{filteredBatches.length}</span> of{' '}
            <span className="font-medium">{batches?.length || 0}</span> batches
          </div>
        </div>
      </div>
    </div>
  );
};
